import React, { useEffect, useState } from "react";
import InputField from "../../CommonComponents/InputField";
import Button from "../../Button";
import Toast from "../../CommonComponents/Toast";
import useAuth from "../../../Hooks/useAuth";
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import { RESULT_STATUS } from "../../../constants";
import RichTextEditor from "../../CommonComponents/RichTextEditor";

const validators = {
  name: (v) =>
    !v || v?.trim() === ""
      ? "Module name is required"
      : v.length > 100
      ? "Max characters: 100"
      : "",
  description: (v) =>
    !v || v?.trim() === ""
      ? "Description is required"
      : v.length > 300
      ? "Max characters: 300"
      : "",
};

const CreateModule = ({
  setOpenModal,
  lpId = null,
  orderno = null,
  onSave = null,
  onUpdate = null,
  moduleDetail = {},
  setModuleDetail,
  setPathDetails,
}) => {
  const { setShowNotify } = useAuth();
  const axios = useAxiosPrivate();

  const isEdit = Number.isInteger(+moduleDetail.moduleId);

  const [errors, setErrors] = useState({});
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [detail, setDetail] = useState({ name: "", description: "" });
  const [isDisable, setIsDisable] = useState(false);

  useEffect(() => {
    if (isEdit && moduleDetail) {
      setDetail({ ...moduleDetail });
    }
  }, [isEdit, moduleDetail]);

  const onChangeDetails = (field, value) => {
    setDetail((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: validators[field](value),
    }));
  };

  const onSaveOrUpdate = async () => {
    const errors = {};
    Object.entries(detail).forEach(([field, value]) => {
      if (validators[field]) {
        const error = validators[field](value);
        if (error) {
          errors[field] = error;
        }
      }
    });
    setErrors(errors);

    if (Object.keys(errors).length) {
      // toast message for fix the highlighted error
      setToastOpen(true);
      setToastMessage("Please fix the highlighted errors before save");
      return;
    }

    //save api
    try {
      setIsDisable(true);
      if (isEdit) {
        const { data } = await axios.put(
          `node/admin/module/${detail.moduleId}?lpId=${lpId}`,
          { ...detail, orderno },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        if (data.resultCode === RESULT_STATUS.SUCCESS) {
          console.log("data", data);
          onUpdate({ ...data, ...detail });
          setOpenModal(false);
        } else {
          setToastOpen(true);
          setToastMessage(
            data?.msg ?? `Unable to update the module. Please try again later`,
          );
          return;
        }
      } else {
        const { data } = await axios.post(
          `node/admin/module?lpId=${lpId}`,
          { ...detail, orderno:orderno * 100000 },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        if (data.resultCode === RESULT_STATUS.SUCCESS) {
          console.log("data", data);
          onSave({ ...data, ...detail });
          setOpenModal(false);
        } else {
          setToastOpen(true);
          setToastMessage(
            data?.msg ?? `Unable to create the module. Please try again later`,
          );
          return;
        }
      }
    } catch (error) {
      console.error("error in create module", error);
      let isUnAuth = error.message.includes("403");
      if (isUnAuth) {
        setShowNotify({
          show: true,
          msg: "You have been logged-out due to inactivity. Login again.",
          title: "Warning !",
          isUnAuth,
        });
        setOpenModal(false);
        return;
      }
      setToastOpen(true);
      setToastMessage(
        error.message ??
          `Unable to ${
            isEdit ? "update" : "create"
          } the module. Please try again later`,
      );
    } finally {
      setIsDisable(false);
    }
  };

  const buttonText = `${
    isEdit
      ? `${isDisable ? "Updating" : "Update"}`
      : `${isDisable ? "Saving" : "Save"}`
  }`;

  return (
    <div className="admin-path-module-create-container">
      <InputField
        label="Module Name"
        placeholder="Enter the name"
        className="admin-path-module-create-name"
        name="name"
        value={detail.name}
        onChange={(event) =>
          onChangeDetails(event.target.name, event.target.value)
        }
        hintText={errors.name}
        destructive={!!errors.name}
      />
      <RichTextEditor
        title="Description"
        className="admin-path-module-create-description"
        value={detail.description}
        name="description"
        onChange={(value) => onChangeDetails("description", value)}
        hintText={errors.description}
        destructive={!!errors.description}
        init={{
          readonly: true,
          menubar: false,
          min_height: 150,
          branding: false,
          statusbar: false,
          elementpath: false,
          license_key: "gpl",
          selector: "textarea",
          allow_html_in_named_anchor: true,
          plugins: [
            "link",
            "help",
            "code",
            "lists",
            "image",
            "table",
            "media",
            "anchor",
            "editor",
            "advlist",
            "preview",
            "charmap",
            "wordcount",
            "fullscreen",
            "autoresize",
            "visualblocks",
            "searchreplace",
            "insertdatetime",
          ],
          toolbar:
            "undo redo | blocks | " +
            "bold italic underline forecolor | link | alignleft aligncenter " +
            "alignright alignjustify | bullist numlist outdent indent | " +
            "removeformat | help |",
          content_style:
            "body { font-family:Inter,Arial,sans-serif; font-size:14px }",
        }}
      />
      <div className="admin-path-module-create-btn-wrapper">
        <Button
          size="sm"
          hierarchy={{ type: "secondaryGrey", buttonText: "Cancel" }}
          onClick={() => setOpenModal(false)}
        />
        <Button
          size="sm"
          hierarchy={{
            type: "primary",
            buttonText: buttonText,
          }}
          onClick={onSaveOrUpdate}
          disable={isDisable}
        />
      </div>
      <Toast
        open={toastOpen}
        type="warning"
        message={toastMessage}
        onClose={() => setToastOpen(false)}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </div>
  );
};

export default CreateModule;
